import React from 'react';
import styles from './Timeline.module.scss';
import moment from 'moment-timezone';
import { timelineTableConfig } from './Timeline.config';
import { Spinner } from 'reactstrap';
import { TimelineScreenshotsLightbox } from './TimelineScreenshotsLightbox.component';
import { DayTimelineCardComponent as DayTimelineCard } from './components/DayTimelineCard.component';
import { ReportFiltersBar } from '@/components/ReportFiltersBar';
import { getActivityColor } from '@/utils/activityColor';
import { formatPercent } from '@/utils/numberUtils';
import { formatTimeFromSeconds } from '@/utils/timeUtils';
import { normalizeIdleForDisplay } from '@/utils/idleDisplayUtils';
import { EmptyState } from '@/components/EmptyState';

// Pure presentational — no Redux/react-query/service calls. Rendered by both
// Timeline.component.jsx (real) and Timeline.demo.component.jsx (static
// fixture-backed); a future redesign only ever needs to touch this file plus
// whichever container's props it changes.
export const TimelineView = ({
  membersOptions,
  selectedMember,
  onMemberFilterChange,
  membersLoading,
  membersError,
  membersErrorObj,
  refetchMembers,
  timezoneOptions,
  selectedTimezone,
  onTimezoneChange,
  timezonesLoading,
  date,
  onDateChange,
  onRefresh,
  onExport,
  exportLoading,
  exportDisabled,
  loading,
  sessionsByDay,
  onOpenScreenshotsModal,
  screenshotModalOpen,
  closeScreenshotsModal,
  activitiesLoading,
  activitiesError,
  flattenedScreens,
  selectedScreenshotIndex,
  setSelectedScreenshotIndex,
  activeSessionMeta,
  employeeName,
  teamName,
}) => {
  return (
    <div className={styles.timelinePage}>
      <ReportFiltersBar
        membersOptions={membersOptions}
        selectedMember={selectedMember}
        onMemberFilterChange={onMemberFilterChange}
        membersLoading={membersLoading}
        membersError={membersError}
        membersErrorObj={membersErrorObj}
        refetchMembers={refetchMembers}
        timezoneOptions={timezoneOptions}
        selectedTimezone={selectedTimezone}
        onTimezoneChange={onTimezoneChange}
        timezonesLoading={timezonesLoading}
        date={date}
        onDateChange={onDateChange}
        onRefresh={onRefresh}
        onExport={onExport}
        exportLoading={exportLoading}
        exportDisabled={exportDisabled}
      />
      {loading && (
        <div className={styles.loaderContainer}>
          <Spinner color="primary" />
        </div>
      )}
      {!loading && !sessionsByDay.length && (
        <EmptyState
          illustration
          title="No Sessions Found"
          description="No sessions found for the selected filters. Try a different date range."
        />
      )}
      {!loading &&
        sessionsByDay.map((day, index) => {
          const segments = day.sessions.map((session) => {
            const startTime = moment(session.start)
              .tz(selectedTimezone)
              .format('HH:mm');
            const endTime = moment(session.end)
              .tz(selectedTimezone)
              .format('HH:mm');
            const { displayIdle, idlePercent } = normalizeIdleForDisplay(
              session.durationSec,
              session.idleSec
            );
            return {
              start12H: moment(session.start)
                .tz(selectedTimezone)
                .format('hh:mm A'),
              end12H: moment(session.end)
                .tz(selectedTimezone)
                .format('hh:mm A'),
              totalTimeTracked: formatTimeFromSeconds(session.durationSec),
              idleTime: formatTimeFromSeconds(displayIdle),
              activityPct: formatPercent(session.activityPct),
              idlePercent: formatPercent(idlePercent),
              start24H: startTime,
              end24H: endTime,
              color: getActivityColor(session.activityPct, true),
            };
          });
          return (
            <DayTimelineCard
              key={day.dayKey}
              day={day}
              index={index}
              segments={segments}
              memberId={selectedMember?.value}
              tableColumns={timelineTableConfig({
                selectedTimezone,
                openScreenshotsModal: onOpenScreenshotsModal,
                dayLabel: day.dayLabel,
              })}
            />
          );
        })}
      <TimelineScreenshotsLightbox
        open={screenshotModalOpen}
        onClose={closeScreenshotsModal}
        activitiesLoading={activitiesLoading}
        activitiesError={activitiesError}
        flattenedScreens={flattenedScreens}
        selectedScreenshotIndex={selectedScreenshotIndex}
        selectedTimezone={selectedTimezone}
        setSelectedScreenshotIndex={setSelectedScreenshotIndex}
        activeSessionMeta={activeSessionMeta}
        employeeName={employeeName}
        teamName={teamName}
      />
    </div>
  );
};
